import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalstorageState } from 'rooks';

import { FEATURE_ANNOUNCEMENT_ATTRIBUTE, FEATURE_ANNOUNCEMENT_ENABLED_STORAGE_KEY, FEATURE_ANNOUNCEMENT_STORAGE_KEY } from './_enums';
import { getAnnouncements, getPendingAnnouncements, getReleasedNews, news, normalizeDismissedIds } from './_helpers';

import { useSelf } from '@/data/user';

// The target of an announcement mounts whenever its page finishes loading, leaves when the
// user navigates on, and can come and go without the route changing at all — a panel
// collapses, a flagged control disappears. Polling is the cheap way to track all of that: a
// pass is one querySelectorAll per announced feature id, and there are two.
//
// The bound that matters is not the interval, it is that the timer only exists while an
// announcement is actually waiting for somewhere to go. A user with nothing pending — which
// is almost everyone, almost all of the time — runs no timer at all.
//
// The obvious alternative, a MutationObserver over the document, would fire on every
// keystroke in the detailed budget grid. A fixed number of scans after each navigation was
// the first attempt and is worse than it looks: a page slower than the last scan never shows
// its tip, and the tip silently not appearing is indistinguishable from working.
const SCAN_INTERVAL_MS = 750;

// An element only anchors a tooltip if it occupies space. A display:none ancestor — an
// inactive tab panel, an unopened dropdown — gives a zero-size rect, and anchoring to one
// parks the tooltip in the corner of the page pointing at nothing.
const isVisible = (element) => {
  const rect = element.getBoundingClientRect();

  return element.isConnected && rect.width > 0 && rect.height > 0;
};

// First visible match wins. An update naming several ids (the same feature on a template and
// on an estimate) resolves to whichever one the current page renders.
const findTarget = (featureIds) =>
  featureIds.flatMap((featureId) => Array.from(document.querySelectorAll(`[${FEATURE_ANNOUNCEMENT_ATTRIBUTE}="${featureId}"]`))).find(isVisible) ?? null;

const isSameTargets = (current, next) =>
  current.length === next.length && current.every((target, index) => target.announcement.id === next[index].announcement.id && target.element === next[index].element);

// Whether release tooltips are shown at all, and the setter behind the switch on the product
// updates page. On by default — a feature nobody is pointed at is the problem this exists to
// solve — but one click turns the whole mechanism off for good.
export const useIsFeatureAnnouncementsEnabled = () => useLocalstorageState(FEATURE_ANNOUNCEMENT_ENABLED_STORAGE_KEY, true);

// Drives the one tooltip on screen. Mounted once, by ViewFeatureAnnouncements.
//
// Announcements are ordered newest first and shown strictly one at a time: landing on the
// detailed budget after two releases should not put two popovers on the page. Advancing is
// the user's move and never a timer's — Next steps to the one behind it, and closing marks the
// one on screen as seen and quiets the rest until the app is reloaded.
export const useFeatureAnnouncements = () => {
  const location = useLocation();
  const self = useSelf(false);
  const [isEnabled] = useIsFeatureAnnouncementsEnabled();
  const [dismissedIds, setDismissedIds] = useLocalstorageState(FEATURE_ANNOUNCEMENT_STORAGE_KEY, []);
  const [isSnoozed, setIsSnoozed] = useState(false);
  const [targets, setTargets] = useState([]);

  const pending = useMemo(
    () => (!isEnabled || isSnoozed ? [] : getPendingAnnouncements(getAnnouncements(getReleasedNews(news, self.data), new Date()), dismissedIds)),
    [dismissedIds, isEnabled, isSnoozed, self.data]
  );

  const scan = useCallback(() => {
    const next = pending.map((announcement) => ({ announcement, element: findTarget(announcement.featureIds) })).filter((target) => target.element);

    // Compared rather than replaced: a fresh array every scan would re-anchor the popover
    // once a second and restart its entrance animation each time.
    setTargets((current) => (isSameTargets(current, next) ? current : next));
  }, [pending]);

  // Re-run on navigation as well as on the interval, so a tooltip whose target has just left
  // the page goes with it rather than hanging over the next route for up to a scan.
  useEffect(() => {
    if (!pending.length) {
      setTargets([]);
      return undefined;
    }

    scan();
    const interval = setInterval(scan, SCAN_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [pending, scan, location.pathname]);

  const [current] = targets;

  const dismiss = useCallback(() => {
    if (!current) {
      return;
    }

    setDismissedIds((stored) => [...normalizeDismissedIds(stored), current.announcement.id]);
  }, [current, setDismissedIds]);

  // Closing is "seen it, and not now for the rest": the one on screen is stored as dismissed,
  // the same as Got It, so the X does not bring the same tooltip back on every reload. The ones
  // queued behind it were never shown, so they are only quieted for this visit.
  const close = useCallback(() => {
    dismiss();
    setIsSnoozed(true);
  }, [dismiss]);

  return {
    announcement: current?.announcement ?? null,
    element: current?.element ?? null,
    remainingCount: targets.length,
    dismiss,
    close
  };
};

// The spotlight cuts a hole in the dimmed page at the announced element, so it has to follow
// that element's box: the page scrolls, a panel opens, the window resizes, and the hole has
// to still be over the thing the popover is pointing at. Measured on an animation frame
// rather than on scroll and resize listeners because the two miss a layout that nothing
// dispatched an event for — a sheet animating open, a table finishing its virtualization
// pass — and a hole one row off its target is worse than no hole at all.
//
// The loop only exists while a tooltip is on screen. That is the bound that matters: no
// announcement means no element, which means no frame callback.
export const useFeatureAnnouncementTargetRect = (element) => {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!element) {
      setRect(null);
      return undefined;
    }

    let frame;

    const measure = () => {
      const { top, left, width, height } = element.getBoundingClientRect();

      // Replaced only when it has actually moved: a new object every frame would re-render
      // the overlay 60 times a second for a box that is usually sitting still.
      setRect((current) =>
        current && current.top === top && current.left === left && current.width === width && current.height === height ? current : { top, left, width, height }
      );

      frame = requestAnimationFrame(measure);
    };

    measure();

    return () => cancelAnimationFrame(frame);
  }, [element]);

  return rect;
};
